import { Component, OnInit, OnChanges, SimpleChanges, Input, AfterViewInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from 'src/services/logger-services/logger.service';
// Declare Plotly as global variable
declare const Plotly: any;

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss']
})
export class ChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  
  @Input() chartData: any;
  @Input() isSmall: boolean = false; // New input to control chart size
  public isChartReady = false;
  public chartError: string | null = null;
  private plotlyInstance: any = null;
  private retryCount = 0;
  private readonly maxRetries = 5;
  private containerCheckAttempts = 0;
  private readonly maxContainerCheckAttempts = 10;
  private readonly ChartComponentError = (error: any) =>
    `[ChartComponent] - ${JSON.stringify(error)}`;

  constructor(
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly loggerService: LoggerService) {}

  ngOnInit(): void {
    this.chartError = null;
    
    if (this.chartData) {
      this.isChartReady = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && this.chartData) {
      this.isChartReady = true;
      this.containerCheckAttempts = 0;
      this.retryCount = 0;
      setTimeout(() => {
        this.waitForContainerAndUpdate();
      }, 100);
    }
    
    // Handle isSmall property changes
    if (changes['isSmall'] && this.isChartReady && this.chartData) {
      this.containerCheckAttempts = 0;
      this.retryCount = 0;
      setTimeout(() => {
        this.waitForContainerAndUpdate();
      }, 100);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.isChartReady && this.chartData) {
        this.waitForContainerAndUpdate();
      }
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.plotlyInstance && this.chartContainer && this.chartContainer.nativeElement) {
      try {
        Plotly.purge(this.chartContainer.nativeElement);
      } catch (error) {
        this.loggerService.logError(this.ChartComponentError(error), "ngOnDestroy");
      }
    }
  }

  private checkPlotlyAvailability(): boolean {
    if (typeof Plotly === 'undefined') {
      const translatedPlotlyErrorMsg = this.translate.instant('PLOTLY_LIBRARY_NOT_LOADED');
      this.chartError = translatedPlotlyErrorMsg;
      return false;
    }
    return true;
  }

  public waitForContainerAndUpdate(): void {
    if (this.chartContainer && this.chartContainer.nativeElement) {
      this.containerCheckAttempts = 0;
      this.updateChart();
      return;
    }

    if (this.containerCheckAttempts < this.maxContainerCheckAttempts) {
      this.containerCheckAttempts++;
      setTimeout(() => {
        this.cdr.detectChanges();
        this.waitForContainerAndUpdate();
      }, 200);
    } else {
      const translatedContainerElementNotFoundErrorMsg = this.translate.instant('CHART_CONTAINER_ELEMENT_NOT_FOUND');
      this.chartError = translatedContainerElementNotFoundErrorMsg;
    }
  }

  private updateChart(): void {
    if (!this.checkPlotlyAvailability()) {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.updateChart(), 1000);
      }
      return;
    }

    if (!this.chartContainer || !this.chartContainer.nativeElement) {
      const translatedContainerNotFoundErrorMsg = this.translate.instant('CHART_CONTAINER_NOT_FOUND');
      this.chartError = translatedContainerNotFoundErrorMsg;
      this.waitForContainerAndUpdate();
      return;
    }

    if (!this.chartData) {
      this.chartError = 'Chart data not available';
      return;
    }

    try {
      const data = this.chartData;
      const plotData = this.getPlotData(data);
      
      // Check if plotData is empty or invalid
      if (!plotData || plotData.length === 0) {
        const translatedNoValidPlotErrorMsg = this.translate.instant('NO_VALID_PLOT_DATA');
        this.chartError = translatedNoValidPlotErrorMsg;
        this.cdr.detectChanges();
        return;
      }
      
      const layout = this.getLayout(data);
      
      const config = this.getConfig();

      // Clear previous chart
      if (this.plotlyInstance) {
        Plotly.purge(this.chartContainer.nativeElement);
      }

      // Create new chart
      this.plotlyInstance = Plotly.newPlot(
        this.chartContainer.nativeElement, 
        plotData, 
        layout, 
        config
      ).then(() => {
        this.chartError = null;
        this.cdr.detectChanges();
      }).catch((error: any) => {
        const translatedCreateChatErrorMsg = this.translate.instant('FAILED_TO_CREATE_CHART');
        this.loggerService.logError(this.ChartComponentError(error), "updateChart");
        this.chartError = translatedCreateChatErrorMsg + error.message;
        this.cdr.detectChanges();
      });

    } catch (error) {
      const translatedRenderChatErrorMsg = this.translate.instant('FAILED_TO_RENDER_CHART');
      this.loggerService.logError(this.ChartComponentError(error), "updateChart");
      this.chartError = translatedRenderChatErrorMsg + error;
      this.cdr.detectChanges();
    }
  }

  private getPlotData(data: any): any[] {
    const chartType = data.type && data.type.toLowerCase() || 'bar';
    
    switch (chartType) {
      case 'bar':
        return this.getBarData(data);
      case 'line':
        return this.getLineData(data);
      case 'scatter':
        return this.getLineData(data);
      case 'pie':
        return this.getPieData(data);
      case 'donut':
        return this.getDonutData(data);
      case 'area':
        return this.getAreaData(data);
      case 'heatmap':
        return this.getHeatmapData(data);
      case 'scatterpolar':
      case 'radar':
        return this.getRadarData(data);
      case 'box':
        return this.getBoxData(data);
      default:
        return this.getBarData(data);
    }
  }

  private getBarData(data: any): any[] {    
    // Create unique truncated category names for display
    const truncatedCategories = this.createUniqueLabels(data.categories, 15);
    
    return data.series.map((series: any, seriesIndex: number) => ({
      x: truncatedCategories, // Use unique truncated names for x-axis
      y: series.data,
      type: 'bar',
      name: series.name,
      customdata: data.categories, // Store original full names for hover
      hovertemplate: '<b>%{customdata}</b><br>' +
                     'Value: %{y}<br>' +
                     '<extra></extra>',
      text: series.data.map((value: number) => value.toString()), // Add text labels above bars
      textposition: 'outside', // Position text above bars
      cliponaxis: false, // Prevent text clipping at chart boundaries
      textfont: {
        size: 10,
        color: data.categories.map((category: string, categoryIndex: number) => {
          if (data.default_styling && data.default_styling.colors && data.default_styling.colors.length > 0) {
            return data.default_styling.colors[categoryIndex % data.default_styling.colors.length];
          }
          return series.color || '#2563eb';
        })
      },
      marker: {
        // For bar charts, use different colors for each category if available
        color: data.categories.map((category: string, categoryIndex: number) => {
          if (data.default_styling && data.default_styling.colors && data.default_styling.colors.length > 0) {
            return data.default_styling.colors[categoryIndex % data.default_styling.colors.length];
          }
          return series.color || '#2563eb';
        })
      }
    }));
  }

  private getLineData(data: any): any[] {
    // Create unique truncated category names for display
    const truncatedCategories = this.createUniqueLabels(data.categories, 15);

    return data.series.map((series: any, index: number) => ({
      x: truncatedCategories,
      y: series.data,
      type: 'scatter',
      mode: 'lines+markers',
      name: series.name,
      customdata: data.categories, // Store original full names for hover
      hovertemplate: '<b>%{customdata}</b><br>' +
                     '<i>%{fullData.name}</i><br>' +
                     'Value: %{y:,.0f}<br>' +
                     '<extra></extra>',
      line: {
        color: series.color || data.default_styling && data.default_styling.colors && data.default_styling.colors[index] || '#2563eb',
        width: 2
      },
      marker: {
        color: series.color || data.default_styling && data.default_styling.colors && data.default_styling.colors[index] || '#2563eb',
        size: 4
      }
    }));
  }

  private getPieData(data: any): any[] {    
    // Validate basic data structure
    if (!data.series || !data.series[0] || !data.series[0].data) {
      this.loggerService.logError(this.ChartComponentError(data), "getPieData");
      return [];
    }
    
    const series = data.series[0];    
    let values: number[];
    let labels: string[];
    
    // Check if data is in object format [{ name: "...", value: ... }] or simple array format
    if (series.data.length > 0 && typeof series.data[0] === 'object' && series.data[0].hasOwnProperty('name') && series.data[0].hasOwnProperty('value')) {
      // Handle object format: [{ name: "North America", value: 450 }, ...]
      values = series.data.map((item: any) => item.value);
      labels = series.data.map((item: any) => item.name);
    } else if (data.categories && Array.isArray(data.categories)) {
      // Handle separate arrays format: data: [450, 380, 250] and categories: ["North America", "Europe", "Asia Pacific"]
      values = series.data;
      labels = data.categories;
      
      // Ensure data and categories have the same length
      if (values.length !== labels.length) {
        this.loggerService.logError(this.ChartComponentError(data), "getPieData");
      }
    } else {
      this.loggerService.logError(this.ChartComponentError(data), "getPieData");
      return [];
    }
        
    const pieData = [{
      values: values,
      labels: labels,
      type: 'pie',
      name: series.name || 'Pie Chart',
      marker: {
        colors: data.default_styling && data.default_styling.colors || ['#2563eb', '#dc2626', '#10b981', '#f59e0b', '#8b5cf6']
      },
      textinfo: 'percent',
      textposition: 'inside',
      textfont: {
        size: 14,
        color: 'white'
      },
      automargin: true,
      hovertemplate: '<b>%{label}</b><br>' +
                     'Value: %{value:,.0f}<br>' +
                     'Percentage: %{percent}<br>' +
                     '<extra></extra>',
    }];
    
    return pieData;
  }

  private getDonutData(data: any): any[] {
    const series = data.series[0];
    
    let values: number[];
    let labels: string[];
    
    // Check if data is in object format [{ name: "...", value: ... }] or simple array format
    if (series.data.length > 0 && typeof series.data[0] === 'object' && series.data[0].hasOwnProperty('name') && series.data[0].hasOwnProperty('value')) {
      // Handle object format: [{ name: "North America", value: 450 }, ...]
      values = series.data.map((item: any) => item.value);
      labels = series.data.map((item: any) => item.name);
    } else {
      // Handle separate arrays format: data: [450, 380, 250] and categories: ["North America", "Europe", "Asia Pacific"]
      values = series.data;
      labels = data.categories;
    }
    
    return [{
      values: values,
      labels: labels,
      type: 'pie',
      name: series.name,
      hole: 0.4,
      marker: {
        colors: data.default_styling && data.default_styling.colors || ['#2563eb', '#dc2626', '#10b981', '#f59e0b', '#8b5cf6']
      },
      textinfo: 'percent',
      textposition: 'inside',
      textfont: {
        size: 14,
        color: 'white'
      },
      automargin: true,
      hovertemplate: '<b>%{label}</b><br>' +
                     'Value: %{value:,.0f}<br>' +
                     'Percentage: %{percent}<br>' +
                     '<extra></extra>',
    }];
  }

  private getAreaData(data: any): any[] {
    // Create unique truncated category names for display
    const truncatedCategories = this.createUniqueLabels(data.categories, 15);

    return data.series.map((series: any, index: number) => ({
      x: truncatedCategories,
      y: series.data,
      type: 'scatter',
      mode: 'lines',
      name: series.name,
      fill: 'tonexty',
      customdata: data.categories, // Store original full names for hover
      hovertemplate: '<b>%{customdata}</b><br>' +
                     '<i>%{fullData.name}</i><br>' +
                     'Value: %{y:,.0f}<br>' +
                     '<extra></extra>',
      line: {
        color: series.color || data.default_styling && data.default_styling.colors && data.default_styling.colors[index] || '#2563eb'
      },
      fillcolor: this.addOpacity(series.color || data.default_styling && data.default_styling.colors && data.default_styling.colors[index] || '#2563eb', 0.3)
    }));
  }

  private getHeatmapData(data: any): any[] {
    const series = data.series[0];
    const matrix = [series.data];
    
    // Create unique truncated category names for display
    const truncatedCategories = this.createUniqueLabels(data.categories, 15);
    
    return [{
      z: matrix,
      x: truncatedCategories, // Use unique truncated names for x-axis
      y: [series.name],
      type: 'heatmap',
      colorscale: 'Viridis',
      showscale: true,
      hoverongaps: false,
      customdata: [data.categories], // Store original full names for hover
      hovertemplate: '<b>%{customdata}</b><br>' +
                     'Value: %{z:,.0f}<br>' +
                     '<extra></extra>',
    }];
  }

  private getRadarData(data: any): any[] {
    // For radar charts, we keep full category names as they're displayed around the perimeter
    return data.series.map((series: any, index: number) => ({
      r: series.data,
      theta: data.categories, // Keep full names for radar chart labels
      type: 'scatterpolar',
      mode: 'lines+markers',
      name: series.name,
      customdata: data.categories, // Store original full names for hover
      hovertemplate: '<b>%{theta}</b><br>' +
                     '<i>%{fullData.name}</i><br>' +
                     'Value: %{r:,.0f}<br>' +
                     '<extra></extra>',
      line: {
        color: series.color || data.default_styling && data.default_styling.colors && data.default_styling.colors[index] || '#2563eb'
      },
      marker: {
        color: series.color || data.default_styling && data.default_styling.colors && data.default_styling.colors[index] || '#2563eb'
      }
    }));
  }

  private getBoxData(data: any): any[] {
    // Create unique truncated category names for display
    const truncatedCategories = this.createUniqueLabels(data.categories, 15);
    
    return data.categories.map((category: string, index: number) => ({
      y: [data.series[0].data[index]],
      type: 'box',
      name: truncatedCategories[index], // Use unique truncated name for display
      customdata: [category], // Store original full name for hover
      hovertemplate: '<b>%{customdata}</b><br>' +
                     'Value: %{y:,.0f}<br>' +
                     '<extra></extra>',
      marker: {
        color: data.default_styling && data.default_styling.colors && data.default_styling.colors[index % data.default_styling.colors.length] || '#2563eb'
      }
    }));
  }

    private getLayout(data: any): any {
      const chartType = (data.type || '').toLowerCase();
      const isPieOrDonut = chartType === 'pie' || chartType === 'donut';
      const isBar = chartType === 'bar';
  
      const baseLayout: any = {
        showlegend: isBar ? false : data?.default_styling?.legend?.show !== false,
        legend: this.buildLegend(isPieOrDonut, data),
        margin: this.buildMargin(isPieOrDonut),
        paper_bgcolor: 'white',
        plot_bgcolor: 'white',
        height: this.isSmall ? 360 : 400,
        autosize: true,
        responsive: true,
        automargin: true
      };
      const isCartesian = !isPieOrDonut && chartType !== 'scatterpolar' && chartType !== 'radar';
      if (isCartesian) {
        const shouldRotateLabels = data.categories?.some((cat: string) => cat.length > 8);
        baseLayout.xaxis = this.buildXAxis(data, shouldRotateLabels);
        baseLayout.yaxis = this.buildYAxis(data);
      }
      return baseLayout;
    }
  
  private buildLegend(isPieOrDonut: boolean, data: any): any {
      const legend: any = {
        orientation: isPieOrDonut ? 'v' : 'h',
        font: { size: this.isSmall ? 8 : 10 }
      };

    if (isPieOrDonut) {
      legend.y = 0.5;
      legend.x = 1.02;
      legend.xanchor = 'left';
      legend.yanchor = 'middle';
    } else {
      legend.y = data?.default_styling?.legend?.position === 'bottom' ? -0.2 : 1.1;
      legend.x = 0.5;
      legend.xanchor = 'center';
      legend.yanchor = 'auto';
    }
  
      return legend;
    }
  
    private buildMargin(isPieOrDonut: boolean): any {
      const rightMargin = isPieOrDonut
        ? (this.isSmall ? 120 : 150)
        : (this.isSmall ? 25 : 30);

      return {
        l: this.isSmall ? 60 : 80,
        r: rightMargin,
        b: this.isSmall ? 80 : 100,
        t: this.isSmall ? 80 : 100,
        pad: 4
      };
    }
  
    private buildXAxis(data: any, rotateLabels: boolean): any {
      return {
        title: {
          text: data.xAxis?.title || '',
          font: {
            size: this.isSmall ? 9 : 11,
            color: '#666'
          }
        },
        tickangle: rotateLabels ? -45 : data.xAxis?.rotation || 0,
    
        gridcolor: 'rgba(0,0,0,0.1)',
        zerolinecolor: 'rgba(0,0,0,0.2)',
        tickfont: {
          size: this.isSmall ? 7 : 9
        },
        automargin: true,
        tickmode: 'linear',
        tick0: 0,
      dtick: 1
      };
    }
  
    private buildYAxis(data: any): any {
      return {
        title: {
          text: data.yAxis?.title || '',
          font: {
            size: this.isSmall ? 9 : 11,
           color: '#666'
          }
        },
        gridcolor: 'rgba(0,0,0,0.1)',
        zerolinecolor: 'rgba(0,0,0,0.2)',
        tickfont: {
          size: this.isSmall ? 7 : 9
        },
        automargin: true
      };
    }


  private getConfig(): any {
    return {
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: [
        'pan2d', 
        'lasso2d', 
        'select2d', 
        'autoScale2d', 
        'toggleSpikelines', 
        'hoverClosestCartesian', 
        'hoverCompareCartesian',
        'zoom2d',
      ],
      toImageButtonOptions: {
        format: 'png',
        filename: 'chart',
        height: 500,
        width: 700,
        scale: 1,
        bgcolor: 'white',
        paper_bgcolor: 'white',
        plot_bgcolor: 'white'
      }
    };
  }

  private addOpacity(color: string, opacity: number): string {
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color;
  }

  private truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  }

  private createUniqueLabels(categories: string[], maxLength: number): string[] {
    const truncatedLabels: string[] = [];
    const seenLabels = new Set<string>();
    
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      let truncatedLabel = this.truncateText(category, maxLength);
      
      // If the truncated label already exists, try to make it unique
      if (seenLabels.has(truncatedLabel)) {
        // Try progressively longer truncations to find a unique label
        for (let len = maxLength + 1; len <= category.length; len++) {
          const longerLabel = this.truncateText(category, len);
          if (!seenLabels.has(longerLabel)) {
            truncatedLabel = longerLabel;
            break;
          }
        }
        
        // If still not unique, append index
        if (seenLabels.has(truncatedLabel)) {
          truncatedLabel = this.truncateText(category, maxLength - 3) + `..${i + 1}`;
        }
      }
      
      truncatedLabels.push(truncatedLabel);
      seenLabels.add(truncatedLabel);
    }
    
    return truncatedLabels;
  }

  public getChartTitle(): string {
    if(this.chartData && this.chartData.title && this.chartData.title.length > 50){
      return this.chartData.title.substring(0,50) + '...';
    }
    return this.chartData && this.chartData.title || '';
  }

  public getChartSubtitle(): string {
    return this.chartData && this.chartData.subtitle || '';
  }

  public hasChartError(): boolean {
    return !!this.chartError;
  }

  public getChartError(): string {
    return this.chartError || '';
  }

  public hasValidData(): boolean {
    if (!this.chartData || !this.chartData.series || this.chartData.series.length === 0) {
      return false;
    }
    
    const series = this.chartData.series[0];
    if (!series.data || series.data.length === 0) {
      return false;
    }
    
    // For pie charts, check if data is in object format or if categories exist
    if (this.chartData.type === 'pie' || this.chartData.type === 'donut') {
      // Check object format: [{ name: "...", value: ... }]
      if (series.data.length > 0 && typeof series.data[0] === 'object' && 
          series.data[0].hasOwnProperty('name') && series.data[0].hasOwnProperty('value')) {
        return true;
      }
      // Check separate arrays format: data + categories
      if (this.chartData.categories && this.chartData.categories.length > 0) {
        return true;
      }
      return false;
    }
    
    // For other chart types, categories should exist
    return this.chartData.categories && this.chartData.categories.length > 0;
  }
} 